import { createSlice } from "@reduxjs/toolkit";
import { TIMER_STATUS } from "./constants";



const initialState = {
    timeCount: new Date().getTime(),
    timerStatus: TIMER_STATUS[0],
    setting: "",

    timeCountEnded: null,
    timeCountStarted: null,

    notes: "",
    strategy: "",
    prompts: "",

    sessionActive: false,

    // Lucket integration
    plannedLuckets: [],           // Array of lucket rowIDs selected pre-session
    activeLuckets: [],            // Array of lucket rowIDs currently being worked on
    lucketTimeSegments: [],       // Array of {lucketRowID, startTime, endTime}
    currentActiveLucketId: null,  // Single lucket rowID that's currently active

    // Mind map integration
    mindMapOpen: false,

    // Risk register integration
    riskRegisterOpen: false,
};

export const timeCounterSlice = createSlice({
    name: 'timeCounter',
    initialState,
    reducers: {
        startTimer: (state) => {
            state.timerStatus = TIMER_STATUS[1];
            state.timeCountStarted = new Date().getTime();
        },
        stopTimer: (state) => {
            state.timerStatus = TIMER_STATUS[0];
            state.timeCountEnded = new Date().getTime();
        },
        startSession: (state) => {
            state.sessionActive = true;
        },
        stopSession: (state) => {
            state.sessionActive = false;
        },
        toggleSession: (state) => {
            if (state.timerStatus === TIMER_STATUS[0]) {
                state.timerStatus = TIMER_STATUS[1];
            }
            else {
                state.timerStatus = TIMER_STATUS[0];
            }
        },
        setSetting: (state, action) => {
            state.setting = action.payload;
        },
        setTimeCount: (state) => {
            state.timeCount = new Date().getTime() - state.timeCount;
        },
        setNotes: (state, action) => {
            state.notes = action.payload;
        },
        setStrategy: (state, action) => {
            state.strategy = action.payload;
        },
        setPrompts: (state, action) => {
            state.prompts = action.payload;
        },

        // Lucket integration reducers
        setPlannedLuckets: (state, action) => {
            // action.payload = array of lucket rowIDs
            state.plannedLuckets = action.payload;
        },

        togglePlannedLucket: (state, action) => {
            // action.payload = lucket rowID
            const lucketId = action.payload;
            if (state.plannedLuckets.includes(lucketId)) {
                state.plannedLuckets = state.plannedLuckets.filter(id => id !== lucketId);
            } else {
                state.plannedLuckets.push(lucketId);
            }
        },

        setActiveLuckets: (state, action) => {
            // action.payload = array of lucket rowIDs
            state.activeLuckets = action.payload;
        },

        setCurrentActiveLucket: (state, action) => {
            // action.payload = lucket rowID or null
            const now = new Date().getTime();

            // End previous lucket segment if exists
            if (state.currentActiveLucketId) {
                const lastSegment = state.lucketTimeSegments[state.lucketTimeSegments.length - 1];
                if (lastSegment && !lastSegment.endTime) {
                    lastSegment.endTime = now;
                }
            }

            // Start new lucket segment
            state.currentActiveLucketId = action.payload;
            if (action.payload) {
                state.lucketTimeSegments.push({
                    lucketRowID: action.payload,
                    startTime: now,
                    endTime: null
                });

                // Add to activeLuckets if not already there
                if (!state.activeLuckets.includes(action.payload)) {
                    state.activeLuckets.push(action.payload);
                }
            }
        },

        addLucketToSession: (state, action) => {
            // action.payload = lucket rowID
            if (!state.activeLuckets.includes(action.payload)) {
                state.activeLuckets.push(action.payload);
            }
        },

        removeLucketFromSession: (state, action) => {
            // action.payload = lucket rowID
            state.activeLuckets = state.activeLuckets.filter(id => id !== action.payload);

            // Also deactivate if currently active
            if (state.currentActiveLucketId === action.payload) {
                state.currentActiveLucketId = null;
                // Close the time segment
                const lastSegment = state.lucketTimeSegments[state.lucketTimeSegments.length - 1];
                if (lastSegment && !lastSegment.endTime) {
                    lastSegment.endTime = new Date().getTime();
                }
            }
        },

        resetSessionLuckets: (state) => {
            state.plannedLuckets = [];
            state.activeLuckets = [];
            state.lucketTimeSegments = [];
            state.currentActiveLucketId = null;
        },

        toggleMindMap: (state) => {
            state.mindMapOpen = !state.mindMapOpen;
            if (state.mindMapOpen) state.riskRegisterOpen = false;
        },

        setMindMapOpen: (state, action) => {
            state.mindMapOpen = action.payload;
        },

        toggleRiskRegister: (state) => {
            state.riskRegisterOpen = !state.riskRegisterOpen;
            if (state.riskRegisterOpen) state.mindMapOpen = false;
        },

        setRiskRegisterOpen: (state, action) => {
            state.riskRegisterOpen = action.payload;
        },
    },
});


export const {
    setPrompts,
    toggleSession,
    startSession,
    stopSession,
    setSetting,
    setStrategy,
    setNotes,
    startTimer,
    setTimeCount,
    stopTimer,
    setPlannedLuckets,
    togglePlannedLucket,
    setActiveLuckets,
    setCurrentActiveLucket,
    addLucketToSession,
    removeLucketFromSession,
    resetSessionLuckets,
    toggleMindMap,
    setMindMapOpen,
    toggleRiskRegister,
    setRiskRegisterOpen,
} = timeCounterSlice.actions;

export const selectTimeCount = (state) => state.timeCounter.timeCount;
export const selectTimerStatus = (state) => state.timeCounter.timerStatus;
export const selectTimeCountEnded = (state) => state.timeCounter.timeCountEnded;
export const selectTimeCountStarted = (state) => state.timeCounter.timeCountStarted;
export const selectNotes = (state) => state.timeCounter.notes;
export const selectPrompts = (state) => state.timeCounter.prompts;
export const selectStrategy = (state) => state.timeCounter.strategy;
export const selectSetting = (state) => state.timeCounter.setting;
export const selectSessionActive = (state) => state.timeCounter.sessionActive;
export const selectPlannedLuckets = (state) => state.timeCounter.plannedLuckets;
export const selectActiveLuckets = (state) => state.timeCounter.activeLuckets;
export const selectCurrentActiveLucketId = (state) => state.timeCounter.currentActiveLucketId;
export const selectLucketTimeSegments = (state) => state.timeCounter.lucketTimeSegments;
export const selectMindMapOpen = (state) => state.timeCounter.mindMapOpen;
export const selectRiskRegisterOpen = (state) => state.timeCounter.riskRegisterOpen;

export default timeCounterSlice.reducer;
